import React, { useState, useCallback, useEffect, useRef } from 'react'
import { library } from '@fortawesome/fontawesome-svg-core'
import { fas } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Chat from '@shared/Chat'
import { pathname, getRequest, postRequest, patchRequest } from '@react/helpers/fetch'

library.add(fas)

const t = key => I18n.t(`ticket_assistant.${key}`)

const Assistant = ({ ticketId, canDecide = false, maxSizeMb, requesterName = '' }) => {
  const [chat, setChat]         = useState(null)
  const [deciding, setDeciding] = useState(false)
  const decidedRef              = useRef(false)

  const url = path => pathname({ path: `/tickets/${ticketId}/assistant${path}`, format: 'json' })

  const load = useCallback(() => getRequest(url('')).then(data => { setChat(data); return data }), [ticketId])
  const refresh = useCallback(() => { load() }, [load])

  const decide = action => {
    decidedRef.current = true
    setDeciding(true)
    patchRequest({ url: url(`/${action}`) })
      .then(setChat)
      .finally(() => setDeciding(false))
  }

  const open      = !chat || chat.state === 'open'
  const decidable = canDecide && chat?.decidable
  const finished  = chat && !open

  // Handed to the support without the person asking: they get the ordinary ticket page.
  useEffect(() => {
    if (finished && !decidedRef.current) window.location.reload()
  }, [finished])

  return (
    <div className="ticket-assistant ticket-assistant--card">
      <div className="ticket-assistant__chat chat">
        <Chat
          key         = {ticketId}
          channel     = "TicketAssistantChannel"
          placeholder = {t('placeholder')}
          onShow      = {load}
          onMessages  = {body => postRequest({ url: url('/messages'), body }).then(data => { setChat(data); return data })}
          onDone      = {refresh}
          maxSizeMb   = {maxSizeMb}
          hideInput   = {!canDecide || !open}
          pendingNote = {t('received')}
          rows
          userName      = {requesterName}
          assistantName = {t('name')}
          enterSends    = {false}
        />
      </div>

      { decidable &&
        <div className="ticket-assistant__decision">
          <div className="min-w-0 flex-1">
            <div className="ticket-assistant__question">{t('question')}</div>
            <div className="ticket-assistant__hint">{t('question_hint')}</div>
          </div>
          <div className="ticket-assistant__actions">
            <button type="button" className="btn btn-outline-default" disabled={deciding} onClick={() => decide('escalate')}>
              <FontAwesomeIcon icon="headset" /> {t('escalate')}
            </button>
            <button type="button" className="btn btn-primary" disabled={deciding} onClick={() => decide('resolve')}>
              <FontAwesomeIcon icon="check" /> {t('resolve')}
            </button>
          </div>
        </div>
      }

      { finished &&
        <div className={`ticket-assistant__outcome ticket-assistant__outcome--${chat.state}`}>
          <FontAwesomeIcon icon={chat.state === 'resolved' ? 'check-circle' : 'headset'} />
          <div className="min-w-0 flex-1">
            <div className="ticket-assistant__outcome-title">{t(`outcome.${chat.state}.title`)}</div>
            <div className="ticket-assistant__outcome-text">{t(`outcome.${chat.state}.text`)}</div>
          </div>
          { chat.state === 'escalated' && canDecide &&
            <a className="btn btn-primary" href={`/tickets/${ticketId}`}>{t('write_to_support')}</a>
          }
        </div>
      }
    </div>
  )
}

export default Assistant
